/* -----------------Globals--------------- */
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import {
	Keyboard,
	type KeyboardEvent,
	ScrollView,
	type ScrollViewProps,
} from "react-native";

/* -----------------Types--------------- */
export type CustomKeyboardAvoidingViewRef = {
	scrollTo: (position: number) => void;
};

/* -----------------Props--------------- */
type Props = {
	children: React.ReactNode;
	scrollPosition?: number;
} & ScrollViewProps;

/* -----------------Component--------------- */
const CustomKeyboardAvoidingView = forwardRef<
	CustomKeyboardAvoidingViewRef,
	Props
>((props, ref) => {
	const { children, scrollPosition, ...rest } = props;

	const scrollViewRef = useRef<ScrollView | null>(null);

	useImperativeHandle(
		ref,
		() => ({
			scrollTo: (position: number) => {
				scrollViewRef.current?.scrollTo({
					y: position,
					animated: true,
				});
			},
		}),
		[],
	);

	const onKeyboardDidShow = React.useCallback(
		(event: KeyboardEvent) => {
			const keyboardHeight = event.endCoordinates.height;
			scrollViewRef.current?.scrollTo({
				x: 0,
				y: scrollPosition ? scrollPosition : keyboardHeight + 100,
				animated: true,
			});
		},
		[scrollPosition],
	);

	const onKeyboardDidHide = React.useCallback(() => {
		scrollViewRef.current?.scrollTo({
			x: 0,
			y: 0,
			animated: true,
		});
	}, []);

	React.useEffect(() => {
		const keyboardDidShowListener = Keyboard.addListener(
			"keyboardDidShow",
			onKeyboardDidShow,
		);
		const keyboardDidHideListener = Keyboard.addListener(
			"keyboardDidHide",
			onKeyboardDidHide,
		);

		return () => {
			keyboardDidShowListener.remove();
			keyboardDidHideListener.remove();
		};
	}, [onKeyboardDidShow, onKeyboardDidHide]);

	return (
		<ScrollView
			contentContainerStyle={{
				flexGrow: 1,
			}}
			ref={scrollViewRef}
			keyboardShouldPersistTaps="always"
			scrollToOverflowEnabled
			{...rest}
		>
			{children}
		</ScrollView>
	);
});

CustomKeyboardAvoidingView.displayName = "CustomKeyboardAvoidingView";

export default CustomKeyboardAvoidingView;
